import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, getToken, getUser } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, Trash2, RotateCcw, Download, FileArchive, ShieldAlert, ClipboardList,
  BellRing, CheckSquare, Square, Filter, PowerOff, Power, RefreshCw, Package, DatabaseZap,
} from "lucide-react";
import { toast } from "sonner";
import DataManagementCard from "@/components/DataManagementCard";

const ENTITIES = [
  { value: "subscribers",     label: "Subscribers" },
  { value: "helpers",         label: "Helpers" },
  { value: "sellers",         label: "Sellers" },
  { value: "purchasers",      label: "Purchasers" },
  { value: "animals",         label: "Animals" },
  { value: "cashcollections", label: "Receipts / Cash" },
  { value: "gatepasses",      label: "QR Gate Passes" },
  { value: "subscriptions",   label: "Subscriptions" },
  { value: "vehicles",        label: "Vehicles (read-only)" },
  { value: "notifications",   label: "Notifications" },
];

const COLUMNS = {
  subscribers:     ["haat_name", "owner_name", "owner_mobile", "plan_id", "subscription_status"],
  helpers:         ["name", "mobile", "subscriber_id", "is_active"],
  sellers:         ["name", "mobile", "village", "district", "state"],
  purchasers:      ["name", "mobile", "village", "district", "state"],
  animals:         ["registration_number", "animal_type", "breed", "gender", "ear_tag", "status"],
  cashcollections: ["receipt_number", "amount", "payment_type", "purpose"],
  gatepasses:      ["pass_number", "status", "animal_id", "entry_time", "exit_time"],
  subscriptions:   ["haat_name", "owner_mobile", "plan_id", "subscription_status", "is_active"],
  vehicles:        ["vehicle_number", "driver_name", "driver_mobile", "animal_id"],
  notifications:   ["template", "to", "channel", "status"],
};

const idOf = (r) => r.subscriber_id || r.helper_id || r.seller_id || r.purchaser_id ||
                    r.animal_id || r.collection_id || r.gatepass_id || r.notification_id || "";

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "bg-transparent hover:bg-accent border-border"}`}
    >
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

/* ============ RECORDS TAB ============ */
function RecordsTab() {
  const user = getUser() || {};
  const [entity, setEntity] = useState("subscribers");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {ids:[], reason}
  const [confirmCode, setConfirmCode] = useState("");
  const [sentCode, setSentCode] = useState("");
  const [subActionSub, setSubActionSub] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/records/${entity}`, { params: { q, status, limit: 200 } });
      setItems(r.data.items || []);
      setSelected(new Set());
    } catch (err) {
      console.debug(err);
      toast.error("Load failed");
    } finally { setLoading(false); }
  }, [entity, q, status]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const c = new Set(s);
      if (c.has(id)) c.delete(id); else c.add(id);
      return c;
    });
  };
  const allSelected = items.length > 0 && items.every((r) => selected.has(idOf(r)));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map(idOf)));

  const requestCode = async () => {
    const r = await api.post("/admin/delete-code");
    // Backend only echoes the code in non-production environments.
    if (r.data.code) {
      setSentCode(r.data.code);
      toast.info(`Delete code (dev): ${r.data.code}`);
    } else {
      toast.info("Delete code sent to your registered channel");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget || !confirmCode) { toast.error("Enter the delete code"); return; }
    try {
      if (deleteTarget.ids.length === 1) {
        await api.post(`/admin/records/${entity}/${deleteTarget.ids[0]}/delete`, { reason: deleteTarget.reason, confirm_code: confirmCode });
      } else {
        await api.post(`/admin/records/${entity}/bulk`, { ids: deleteTarget.ids, action: "delete", reason: deleteTarget.reason, confirm_code: confirmCode });
      }
      toast.success(`Deleted ${deleteTarget.ids.length} record(s)`);
      setDeleteTarget(null); setConfirmCode(""); setSentCode(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const doRestore = async (ids) => {
    try {
      if (ids.length === 1) {
        await api.post(`/admin/records/${entity}/${ids[0]}/restore`);
      } else {
        await api.post(`/admin/records/${entity}/bulk`, { ids, action: "restore" });
      }
      toast.success(`Restored ${ids.length} record(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  const exportCurrent = (fmt) => {
    const url = `${API_BASE}/admin/export/${entity}?fmt=${fmt}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${entity}.${fmt}`;
        a.click();
      });
  };

  const subscriptionAction = async (subscriberId, action, extra = {}) => {
    try {
      await api.post(`/admin/subscribers/${subscriberId}/subscription`, { action, ...extra });
      toast.success(`Subscription: ${action}`);
      load();
      setSubActionSub(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const cols = COLUMNS[entity] || ["_id"];

  return (
    <>
      {/* Controls */}
      <div className="am-card p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="grow min-w-[180px]">
            <Label className="text-xs uppercase tracking-widest font-bold">Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grow min-w-[220px]">
            <Label className="text-xs uppercase tracking-widest font-bold">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, mobile, receipt no, tag…" className="pl-9" data-testid="admin-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="rounded-full" onClick={load}><Filter className="w-4 h-4 mr-1.5" />Apply</Button>
        </div>

        {/* Bulk actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground self-center">Bulk ({selected.size}):</div>
          <Button size="sm" variant="outline" className="rounded-full" disabled={selected.size === 0}
                  onClick={() => setDeleteTarget({ ids: [...selected], reason: "" })} data-testid="admin-bulk-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" disabled={selected.size === 0}
                  onClick={() => doRestore([...selected])} data-testid="admin-bulk-restore">
            <RotateCcw className="w-4 h-4 mr-1" /> Restore
          </Button>
          <div className="mx-2 self-center h-6 border-l" />
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCurrent("csv")}><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCurrent("xlsx")}><Download className="w-4 h-4 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCurrent("json")}><Download className="w-4 h-4 mr-1" />JSON</Button>
        </div>
      </div>

      {/* Table */}
      <div className="am-card p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
              <th className="px-3 py-3">
                <button onClick={toggleAll} className="flex items-center">
                  {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
              </th>
              {cols.map((c) => <th key={c} className="px-3 py-3">{c.replace(/_/g, " ")}</th>)}
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols.length + 2} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={cols.length + 2} className="py-8 text-center text-muted-foreground">No records</td></tr>
            )}
            {!loading && items.map((r) => {
              const id = idOf(r);
              const deleted = r.status === "deleted";
              return (
                <tr key={id} className={`border-b last:border-0 ${deleted ? "opacity-60 bg-muted/20" : ""}`}>
                  <td className="px-3 py-3">
                    <button onClick={() => toggle(id)}>
                      {selected.has(id) ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                    </button>
                  </td>
                  {cols.map((c) => (
                    <td key={c} className="px-3 py-3 truncate max-w-[200px]">
                      {typeof r[c] === "boolean" ? (r[c] ? "yes" : "no") : String(r[c] ?? "—")}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {entity === "subscriptions" && !deleted && (
                      <Button size="sm" variant="ghost" onClick={() => setSubActionSub(r)} data-testid={`admin-sub-actions-${id}`}>
                        <Power className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {deleted ? (
                      <Button size="sm" variant="ghost" onClick={() => doRestore([id])} data-testid={`admin-restore-${id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget({ ids: [id], reason: "" })} data-testid={`admin-delete-${id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1 text-destructive" /> Delete
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation dialog — double confirmation + OTP-style code */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setConfirmCode(""); setSentCode(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-destructive" /> Confirm delete</DialogTitle>
            <DialogDescription>
              You are about to soft-delete <b>{deleteTarget?.ids.length}</b> {entity} record(s). The record stays recoverable in the Deleted view.
              Signed-in as <span className="font-mono">{user.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold">Reason (audit log)</Label>
              <Input value={deleteTarget?.reason || ""} onChange={(e) => setDeleteTarget({ ...deleteTarget, reason: e.target.value })} placeholder="Why is this being deleted?" data-testid="admin-delete-reason" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold">Delete confirmation code</Label>
              <div className="flex gap-2">
                <Input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="font-mono tracking-widest text-center" data-testid="admin-delete-code" />
                <Button variant="outline" className="rounded-full" onClick={requestCode}>Send code</Button>
              </div>
              {sentCode && <p className="text-xs text-muted-foreground mt-1">Test code (dev): <span className="font-mono font-bold">{sentCode}</span></p>}
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setDeleteTarget(null); setConfirmCode(""); setSentCode(""); }}>Cancel</Button>
            <Button className="bg-destructive text-destructive-foreground rounded-full" onClick={doDelete} data-testid="admin-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Confirm delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription control */}
      <Dialog open={!!subActionSub} onOpenChange={(o) => !o && setSubActionSub(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Subscription — {subActionSub?.haat_name}</DialogTitle>
            <DialogDescription>Owner {subActionSub?.owner_mobile} · Current plan {subActionSub?.plan_id}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button className="am-btn-primary" onClick={() => subscriptionAction(subActionSub.subscriber_id, "activate")}><Power className="w-4 h-4 mr-1" />Activate</Button>
            <Button variant="outline" className="rounded-full" onClick={() => subscriptionAction(subActionSub.subscriber_id, "suspend")}><PowerOff className="w-4 h-4 mr-1" />Suspend</Button>
            <Button variant="outline" className="rounded-full" onClick={() => subscriptionAction(subActionSub.subscriber_id, "renew", { months: 1 })}><RefreshCw className="w-4 h-4 mr-1" />Renew 1 mo</Button>
            <Button variant="outline" className="rounded-full" onClick={() => subscriptionAction(subActionSub.subscriber_id, "expire")}>Expire</Button>
            <div className="col-span-2 pt-2 border-t">
              <Label className="text-xs uppercase tracking-widest font-bold">Change plan</Label>
              <div className="flex gap-2 mt-1">
                {["starter", "professional", "enterprise"].map((p) => (
                  <Button key={p} size="sm" variant="outline" className="rounded-full capitalize" onClick={() => subscriptionAction(subActionSub.subscriber_id, "change_plan", { plan_id: p })}>
                    {p}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ============ AUDIT TAB ============ */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="am-card p-2 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
            <th className="px-3 py-3">Date · Time</th>
            <th className="px-3 py-3">Actor</th>
            <th className="px-3 py-3">Action</th>
            <th className="px-3 py-3">Entity</th>
            <th className="px-3 py-3">Item</th>
            <th className="px-3 py-3">Reason</th>
            <th className="px-3 py-3">Device</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.log_id} className="border-b last:border-0">
              <td className="px-3 py-3 font-mono text-xs">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
              <td className="px-3 py-3 font-mono text-xs">{l.actor_user_id?.slice(0, 8) || "—"}</td>
              <td className="px-3 py-3"><span className="am-chip">{l.action}</span></td>
              <td className="px-3 py-3">{l.entity}</td>
              <td className="px-3 py-3 font-mono text-xs">{l.entity_id || "—"}</td>
              <td className="px-3 py-3 truncate max-w-[240px]">{l.reason || "—"}</td>
              <td className="px-3 py-3 truncate max-w-[240px] text-xs text-muted-foreground">{l.device || "—"}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No audit entries yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ============ BACKUP TAB ============ */
function BackupTab() {
  const [busy, setBusy] = useState(false);
  const download = async (path, filename) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      toast.success("Backup ready");
    } catch (err) {
      toast.error("Backup failed");
      console.debug(err);
    } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="am-card p-6">
        <div className="w-11 h-11 rounded-2xl bg-accent grid place-items-center mb-3"><FileArchive className="w-5 h-5 text-primary" /></div>
        <h3 className="font-display font-bold text-lg">Firestore snapshot</h3>
        <p className="text-sm text-muted-foreground mt-1">Full JSON dump of every AnimalMela collection. Store offline as an audit-friendly backup.</p>
        <Button className="am-btn-primary mt-4" disabled={busy} onClick={() => download("/admin/backup", `animalmela-backup-${Date.now()}.json`)} data-testid="admin-backup-firestore">
          <Download className="w-4 h-4 mr-1.5" /> Download backup
        </Button>
      </div>
      <div className="am-card p-6">
        <div className="w-11 h-11 rounded-2xl bg-accent grid place-items-center mb-3"><Package className="w-5 h-5 text-primary" /></div>
        <h3 className="font-display font-bold text-lg">Bulk exports</h3>
        <p className="text-sm text-muted-foreground mt-1">Grab any single entity as CSV / Excel / JSON without needing to open the Records tab.</p>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {ENTITIES.slice(0, 6).map((e) => (
            <div key={e.value} className="flex items-center justify-between text-xs">
              <span className="font-semibold">{e.label}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-full h-7 px-2" onClick={() => download(`/admin/export/${e.value}?fmt=csv`, `${e.value}.csv`)}>CSV</Button>
                <Button size="sm" variant="outline" className="rounded-full h-7 px-2" onClick={() => download(`/admin/export/${e.value}?fmt=xlsx`, `${e.value}.xlsx`)}>XLS</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ NOTIFICATIONS TAB ============ */
function NotificationsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/notifications").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="am-card p-2 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
            <th className="px-3 py-3">Template</th>
            <th className="px-3 py-3">Channel</th>
            <th className="px-3 py-3">To</th>
            <th className="px-3 py-3">Status</th>
            <th className="px-3 py-3">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => (
            <tr key={n.notification_id} className="border-b last:border-0">
              <td className="px-3 py-3 font-semibold">{n.template}</td>
              <td className="px-3 py-3"><span className="am-chip">{n.channel}</span></td>
              <td className="px-3 py-3 font-mono text-xs">{n.to}</td>
              <td className="px-3 py-3"><span className={`am-chip ${n.status === "sent" ? "am-chip-ok" : ""}`}>{n.status}</span></td>
              <td className="px-3 py-3 text-xs font-mono">{(n.created_at || "").slice(0, 19).replace("T", " ")}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No notifications</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ============ REPORTS TAB ============ */
function ReportsTab() {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/reports/summary", { params: { period } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [period]);
  const download = (path, filename) => {
    fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
      });
  };
  return (
    <div className="space-y-4">
      <div className="am-card p-4 flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Period:</span>
        {["daily", "weekly", "monthly"].map((p) => (
          <Button key={p} size="sm" variant={period === p ? "default" : "outline"} className={`rounded-full capitalize ${period === p ? "am-btn-primary" : ""}`} onClick={() => setPeriod(p)}>
            {p}
          </Button>
        ))}
      </div>
      {data && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="am-card p-5"><div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Animals registered</div><div className="mt-2 font-display text-3xl font-extrabold">{data.animals_registered}</div></div>
          <div className="am-card p-5"><div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Cash collections</div><div className="mt-2 font-display text-3xl font-extrabold">{data.cash_collections}</div></div>
          <div className="am-card p-5"><div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Cash total (₹)</div><div className="mt-2 font-display text-3xl font-extrabold">₹ {Number(data.cash_amount).toLocaleString("en-IN")}</div></div>
          <div className="am-card p-5"><div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">New subscribers</div><div className="mt-2 font-display text-3xl font-extrabold">{data.new_subscribers}</div></div>
        </div>
      )}
      <div className="am-card p-5 flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground self-center mr-2">Export:</span>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(`/admin/export/animals?fmt=xlsx`, `animals-${period}.xlsx`)}><Download className="w-4 h-4 mr-1" /> Animals Excel</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(`/admin/export/cashcollections?fmt=xlsx`, `cash-${period}.xlsx`)}><Download className="w-4 h-4 mr-1" /> Cash Excel</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(`/admin/export/animals?fmt=csv`, `animals-${period}.csv`)}><Download className="w-4 h-4 mr-1" /> Animals CSV</Button>
      </div>
    </div>
  );
}

/* ============ DATA RESET TAB (Super Admin) ============ */
function DataResetTab() {
  const [subs, setSubs] = useState([]);
  const [sid, setSid] = useState("");
  const [haats, setHaats] = useState([]);
  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setSubs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sid) { setHaats([]); return; }
    api.get(`/admin/subscribers/${sid}/haats`).then((r) => setHaats(r.data.haats || [])).catch(() => setHaats([]));
  }, [sid]);
  return (
    <div className="space-y-4">
      <div className="am-card p-4 max-w-md">
        <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Subscriber (Pashu Haat Owner)</Label>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger className="am-input mt-1" data-testid="dm-admin-sub-select"><SelectValue placeholder="Select subscriber" /></SelectTrigger>
          <SelectContent>
            {subs.map((s) => (
              <SelectItem key={s.subscriber_id} value={s.subscriber_id}>
                {s.haat_name} — {s.owner_name} ({s.owner_mobile})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {sid && haats.length > 0 && <DataManagementCard key={sid} haats={haats} />}
      {sid && haats.length === 0 && <p className="text-sm text-muted-foreground px-1">No active haats for this subscriber.</p>}
    </div>
  );
}

/* ============ PAGE ============ */
export default function AdminRecords() {
  const [tab, setTab] = useState("records");
  return (
    <DashboardShell title="Records & Controls" subtitle="Super Admin console">
      <div className="flex flex-wrap gap-2 mb-4">
        <TabBtn active={tab === "records"} onClick={() => setTab("records")} icon={ClipboardList}>Records</TabBtn>
        <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} icon={ShieldAlert}>Audit Logs</TabBtn>
        <TabBtn active={tab === "backup"} onClick={() => setTab("backup")} icon={FileArchive}>Backup & Export</TabBtn>
        <TabBtn active={tab === "reports"} onClick={() => setTab("reports")} icon={Download}>Reports</TabBtn>
        <TabBtn active={tab === "notifications"} onClick={() => setTab("notifications")} icon={BellRing}>Notifications</TabBtn>
        <TabBtn active={tab === "datareset"} onClick={() => setTab("datareset")} icon={DatabaseZap}>Data Reset</TabBtn>
      </div>
      {tab === "records" && <RecordsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "backup" && <BackupTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "datareset" && <DataResetTab />}
    </DashboardShell>
  );
}
